import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { MetricDetailsSettingsTab } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didUpdate from 'webapp/modifiers/did-update';

interface Args {
	tabs: MetricDetailsSettingsTab[];
	defaultTab?: MetricDetailsSettingsTab;
	badgeCounts?: Partial<Record<MetricDetailsSettingsTab, number>>;
}

interface SettingsTabState {
	isVisualize: boolean;
	isMethodology: boolean;
	isAnnotate: boolean;
	isAbout: boolean;
}

interface MetricDetailsSettingsTabsComponentInterface {
	Element: HTMLDivElement;
	Args: Args;
	Blocks: { default: [tab: SettingsTabState] };
}

export default class MetricDetailsSettingsTabsComponent extends Component<MetricDetailsSettingsTabsComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked activeTab: MetricDetailsSettingsTab;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.activeTab = this.args.defaultTab ?? this.args.tabs[0]!;
	}

	get tabState(): SettingsTabState {
		return {
			isVisualize: this.activeTab === MetricDetailsSettingsTab.VISUALIZE,
			isMethodology: this.activeTab === MetricDetailsSettingsTab.METHODOLOGY,
			isAnnotate: this.activeTab === MetricDetailsSettingsTab.ANNOTATE,
			isAbout: this.activeTab === MetricDetailsSettingsTab.ABOUT,
		};
	}

	isActiveTab = (tab: MetricDetailsSettingsTab): boolean => {
		return this.activeTab === tab;
	};

	changeTab = (tab: MetricDetailsSettingsTab): void => {
		if (tab !== this.activeTab) {
			this.analyticsService.event(TrackingEvent.METRIC_DETAILS_SIDE_BAR_CHANGED, {
				tab,
			});
		}
		this.activeTab = tab;
	};

	resetInvalidTab = (): void => {
		if (!this.args.tabs.includes(this.activeTab)) {
			this.activeTab = this.args.tabs[0]!;
		}
	};

	getBadgeCount = (tab: MetricDetailsSettingsTab): number => {
		return this.args.badgeCounts?.[tab] ?? 0;
	};

	<template>
		<div
			class="metric-explorer-settings"
			{{didUpdate this.resetInvalidTab @tabs}}
			...attributes
		>
			<div class="metric-explorer-settings__tabs">
				{{#each @tabs as |tab|}}
					<button
						class="metric-explorer-settings__tab
							{{if (this.isActiveTab tab) 'metric-explorer-settings__tab--active'}}"
						type="button"
						{{on "click" (fn this.changeTab tab)}}
					>
						{{tab}}
						{{#if (this.getBadgeCount tab)}}
							<span class="metric-explorer-settings__tab-badge">
								{{this.getBadgeCount tab}}
							</span>
						{{/if}}
					</button>
				{{/each}}
			</div>
			<div class="metric-explorer-settings__content">
				{{yield this.tabState}}
			</div>
		</div>
	</template>
}
